import type { Locale, Market, Role, TenantType, UserStatus } from '@ambernord/shared';

export interface PublicUser {
  id: string;
  tenantId: string;
  email: string;
  role: Role;
  locale: Locale;
  status: UserStatus;
  mfaEnabled: boolean;
  /** Atlikušie rezerves kodi (null, ja MFA izslēgta). */
  mfaRecoveryCodesLeft?: number | null;
  emailVerified: boolean;
}

export interface ApiResult<T> {
  ok: boolean;
  status: number;
  data: T;
}

export interface ErrorBody {
  error?: string;
}

export interface LoginResponse extends ErrorBody {
  ok?: boolean;
  mfaRequired?: boolean;
  user?: PublicUser;
}

export interface MeResponse extends ErrorBody {
  user?: PublicUser;
  mfaPending?: boolean;
}

export interface OkResponse extends ErrorBody {
  ok?: boolean;
}

export interface RegisterPayload {
  email: string;
  password: string;
  legalName: string;
  country: string;
  market: Market;
  locale?: Locale;
  turnstileToken: string;
}

async function toResult<T>(res: Response): Promise<ApiResult<T>> {
  let data: T;
  try {
    data = (await res.json()) as T;
  } catch {
    data = {} as T;
  }
  return { ok: res.ok, status: res.status, data };
}

async function request<T>(path: string, method: string, body?: unknown): Promise<ApiResult<T>> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  return toResult<T>(res);
}

export interface UploadOptions {
  filename: string;
  contentType?: string;
}

/** Neapstrādāta faila augšupielāde: ķermenis = baiti, tips galvenē, nosaukums `X-Filename`. */
async function upload<T>(
  path: string,
  file: File | Blob,
  opts: UploadOptions,
): Promise<ApiResult<T>> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': opts.contentType ?? (file.type || 'application/octet-stream'),
      // Galvenes vērtībai jābūt ISO-8859-1 — serveris tāpat sanitizē uz [\w.-].
      'x-filename': opts.filename.replace(/[^\x20-\x7e]/g, '_'),
    },
    body: file,
  });
  return toResult<T>(res);
}

export const api = {
  get: <T>(path: string) => request<T>(path, 'GET'),
  post: <T>(path: string, body?: unknown) => request<T>(path, 'POST', body),
  put: <T>(path: string, body?: unknown) => request<T>(path, 'PUT', body),
  patch: <T>(path: string, body?: unknown) => request<T>(path, 'PATCH', body),
  del: <T>(path: string) => request<T>(path, 'DELETE'),
  upload,
};

export function login(email: string, password: string, turnstileToken: string) {
  return api.post<LoginResponse>('/api/auth/login', { email, password, turnstileToken });
}

export function mfaVerify(code: string) {
  return api.post<OkResponse>('/api/auth/mfa/verify', { code });
}

export interface MfaSetupResponse extends ErrorBody {
  secret?: string;
  uri?: string;
}

/** Ģenerē jaunu TOTP noslēpumu (serverī glabā šifrētu); pabeidz ar `mfaEnable`. */
export function mfaSetup() {
  return api.post<MfaSetupResponse>('/api/auth/mfa/setup');
}

export interface MfaEnableResponse extends OkResponse {
  /** Rezerves kodi — serveris tos atgriež TIKAI šeit un pēc `mfaRegenerateRecoveryCodes`. */
  recoveryCodes?: string[];
}

export function mfaEnable(code: string) {
  return api.post<MfaEnableResponse>('/api/auth/mfa/enable', { code });
}

/** Jauni rezerves kodi; prasa pašreizējo 6 ciparu TOTP. Vecie kodi zaudē spēku. */
export function mfaRegenerateRecoveryCodes(code: string) {
  return api.post<MfaEnableResponse>('/api/auth/mfa/recovery-codes/regenerate', { code });
}

/** Izslēdz MFA ar derīgu TOTP vai rezerves kodu. */
export function mfaDisable(code: string) {
  return api.post<OkResponse>('/api/auth/mfa/disable', { code });
}

/** Administratora MFA atiestate citam lietotājam (atsauc arī visas tā sesijas). */
export function adminResetMfa(userId: string) {
  return api.post<{ user?: PublicUser } & ErrorBody>(
    `/api/admin/users/${encodeURIComponent(userId)}/mfa/reset`,
  );
}

/** Uzaicinājuma pieņemšana: iestata paroli un aktivizē kontu (bez auto-pieslēgšanās). */
export function acceptInvite(token: string, password: string) {
  return api.post<OkResponse>('/api/auth/accept-invite', { token, password });
}

/** Vienmēr 200 — serveris neizpauž, vai konts eksistē. */
export function requestPasswordReset(email: string) {
  return api.post<OkResponse>('/api/auth/password-reset/request', { email });
}

export function confirmPasswordReset(token: string, password: string) {
  return api.post<OkResponse>('/api/auth/password-reset/confirm', { token, password });
}

export interface InviteResponse extends ErrorBody {
  user?: PublicUser;
  /** false = e-pasta piegādātājs nav konfigurēts (ražošanā bez Resend atslēgas). */
  emailSent?: boolean;
}

export interface InvitePayload {
  email: string;
  role: Role;
  tenantId?: string;
}

export function adminInviteUser(payload: InvitePayload) {
  return api.post<InviteResponse>('/api/admin/users', payload);
}

export function adminResendInvite(userId: string) {
  return api.post<OkResponse & { emailSent?: boolean }>(
    `/api/admin/users/${encodeURIComponent(userId)}/resend-invite`,
  );
}

export function adminDeleteInvite(userId: string) {
  return api.del<{ ok?: boolean; error?: string }>(`/api/admin/users/${userId}`);
}

export interface AdminTenant {
  id: string;
  type: TenantType;
  legalName: string;
  country: string;
}

/** Tikai platform_super/platform_admin (citiem 403). */
export function adminListTenants() {
  return api.get<{ items?: AdminTenant[] } & ErrorBody>('/api/admin/tenants');
}

export function logout() {
  return api.post<OkResponse>('/api/auth/logout');
}

export function me() {
  return api.get<MeResponse>('/api/auth/me');
}

export function register(payload: RegisterPayload) {
  return api.post<OkResponse>('/api/auth/register', payload);
}
